import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
import color from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { ComoSelect } from "../Common/ComoSelect";
import ScheduledTasksTimeline from "./ScheduledTasksTimeline";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import {
  useScheduledTasks,
  useDeleteScheduledTask,
  useToggleScheduledTask,
  useRunScheduledTaskNow,
} from "../../Query/useScheduledTask";
import {
  ScheduledTask,
  ScheduleType,
  ScheduleSource,
} from "../../Models/scheduledTask";
import { ComoSwitch } from "../Common/ComoSwitch";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const ScheduledTasksList = () => {
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { t } = useTranslation("scheduledTasks");
  const [userFilter, setUserFilter] = useState<number | "all" | "none">("all");
  const [enabledFilter, setEnabledFilter] = useState<
    "all" | "enabled" | "disabled"
  >("all");

  const describeSchedule = (task: ScheduledTask): string => {
    switch (task.schedule_type) {
      case ScheduleType.ONCE:
        return t("scheduleOnce", {
          time: task.run_at ?? "—",
        });

      case ScheduleType.INTERVAL:
        return t("scheduleEvery", {
          seconds: task.interval_seconds ?? "?",
        });

      case ScheduleType.CRON:
        return t("scheduleCron", {
          expression: task.cron_expression ?? "—",
        });

      default:
        return t("unknown");
    }
  };
  const { data, isLoading, isError } = useScheduledTasks();
  const { mutate: deleteTask } = useDeleteScheduledTask();
  const { mutate: toggleTask } = useToggleScheduledTask();
  const { mutate: runNow } = useRunScheduledTaskNow();

  const userFilterOptions = useMemo(() => {
    const seen = new Map<number, string>();
    (data ?? []).forEach((t) => {
      if (t.user_id != null && !seen.has(t.user_id)) {
        seen.set(
          t.user_id,
          t.user_label || t.user_email || `User #${t.user_id}`,
        );
      }
    });
    const options: { id: number | "all" | "none"; label: string }[] = [
      { id: "all", label: t("allUsers") },
      { id: "none", label: t("generalNoUser") },
    ];
    seen.forEach((label, id) => options.push({ id, label }));
    return options;
  }, [data]);

  const enabledFilterOptions: {
    id: "all" | "enabled" | "disabled";
    label: string;
  }[] = [
    { id: "all", label: t("allStatuses") },
    { id: "enabled", label: t("enabledOnly") },
    { id: "disabled", label: t("disabledOnly") },
  ];

  const visibleTasks = useMemo(
    () =>
      (data ?? []).filter((t) => {
        const userMatch =
          userFilter === "all"
            ? true
            : userFilter === "none"
              ? t.user_id == null
              : t.user_id === userFilter;
        const enabledMatch =
          enabledFilter === "all"
            ? true
            : enabledFilter === "enabled"
              ? t.enabled
              : !t.enabled;
        return userMatch && enabledMatch;
      }),
    [data, userFilter, enabledFilter],
  );

  const handleConfirmDelete = (confirmed: boolean) => {
    setConfirmationDialogOpen(false);
    if (confirmed && selectedId) {
      deleteTask(selectedId);
    }
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) {
    return <Box sx={{ p: 4, color: color.red700 }}>{t("errorFetch")} </Box>;
  }

  const headerCell = {
    color: color.gray600,
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "uppercase" as const,
  };

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: color.gray50 }}
    >
      <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 1,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <ScheduleIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("title")}
                </Typography>
                <Typography variant="caption" sx={{ color: color.gray500 }}>
                  {data?.length ?? 0}
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1.5}
            >
              <Box sx={{ minWidth: 220 }}>
                <ComoSelect
                  options={userFilterOptions}
                  selectedOption={userFilterOptions.find(
                    (o) => o.id === userFilter,
                  )}
                  onSelect={(option) =>
                    setUserFilter(
                      option.id === "all" || option.id === "none"
                        ? option.id
                        : Number(option.id),
                    )
                  }
                  placeholder={t("filterByUser")}
                  sx={{ mb: 0 }}
                />
              </Box>

              <Box sx={{ minWidth: 170 }}>
                <ComoSelect
                  options={enabledFilterOptions}
                  selectedOption={enabledFilterOptions.find(
                    (o) => o.id === enabledFilter,
                  )}
                  onSelect={(option) =>
                    setEnabledFilter(
                      option.id as "all" | "enabled" | "disabled",
                    )
                  }
                  placeholder={t("filterByStatus")}
                  sx={{ mb: 0 }}
                />
              </Box>

              <IconButton
                onClick={() => navigate("/scheduled_tasks/create")}
                sx={{
                  bgcolor: workspace.accent,
                  color: workspace.primary_dark,
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                  gap: 0.75,
                  "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
                }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                >
                  {t("createNew")}
                </Typography>
              </IconButton>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mb: 1,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.accent}`,
            background: color.white,
            overflow: "hidden",
          }}
        >
          <Accordion
            elevation={0}
            sx={{
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ScheduleIcon
                  sx={{ color: workspace.primary_dark, fontSize: 22 }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: color.primary_dark,
                  }}
                >
                  {t("scheduleTimeline", { count: visibleTasks.length })}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: { xs: 2, sm: 1 },
                borderTop: `1px solid ${color.gray200}`,
              }}
            >
              <ScheduledTasksTimeline tasks={visibleTasks} />
            </AccordionDetails>
          </Accordion>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <TableContainer
            sx={{
              border: `1px solid ${color.gray200}`,
              borderRadius: 2,
              overflow: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: color.gray50 }}>
                  <TableCell sx={headerCell}>{t("name")}</TableCell>
                  <TableCell sx={headerCell}>{t("user")}</TableCell>
                  <TableCell sx={headerCell}>{t("task")}</TableCell>
                  <TableCell sx={headerCell}>{t("schedule")}</TableCell>
                  <TableCell sx={headerCell}>{t("nextRun")}</TableCell>
                  <TableCell sx={headerCell}>{t("lastRun")}</TableCell>
                  <TableCell sx={headerCell}>{t("enabled")}</TableCell>
                  <TableCell sx={headerCell}>{t("actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTasks.map((task) => (
                  <TableRow
                    key={task.id}
                    sx={{ "&:hover": { bgcolor: color.gray50 } }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: color.primary_dark }}
                        >
                          {task.name}
                        </Typography>
                        <Chip
                          label={
                            task.source === ScheduleSource.AGENT
                              ? t("agent")
                              : t("admin")
                          }
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            bgcolor:
                              task.source === ScheduleSource.AGENT
                                ? color.green50
                                : color.gray100,
                            color:
                              task.source === ScheduleSource.AGENT
                                ? color.green700
                                : color.gray600,
                          }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: color.gray600, fontSize: "0.8rem" }}
                      >
                        {task.user_label || task.user_email || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: color.gray600, fontSize: "0.8rem" }}
                      >
                        {task.task_name}
                        {task.agent_name ? ` · ${task.agent_name}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip
                          size="small"
                          label={
                            task.schedule_type === ScheduleType.CRON
                              ? "CRON"
                              : task.schedule_type === ScheduleType.INTERVAL
                                ? "INTERVAL"
                                : "ONCE"
                          }
                          sx={{
                            width: "fit-content",
                            height: 22,
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            bgcolor: color.gray100,
                            color: color.gray700,
                          }}
                        />

                        <Typography
                          variant="caption"
                          sx={{
                            color: color.gray600,
                            maxWidth: 220,
                          }}
                        >
                          {describeSchedule(task)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ color: color.gray500 }}
                      >
                        {task.next_run_at ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography
                          variant="caption"
                          sx={{ color: color.gray500 }}
                        >
                          {task.last_run_at ?? "Never"}
                        </Typography>
                        {task.last_status && (
                          <Tooltip
                            title={
                              task.last_status === "error" && task.last_error
                                ? task.last_error
                                : ""
                            }
                            arrow
                            placement="top"
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  task.last_status === "error"
                                    ? color.red700
                                    : color.green700,
                                fontSize: "0.7rem",
                                cursor:
                                  task.last_status === "error" &&
                                  task.last_error
                                    ? "help"
                                    : "default",
                                textDecoration:
                                  task.last_status === "error" &&
                                  task.last_error
                                    ? "underline dotted"
                                    : "none",
                                display: "inline-block",
                                maxWidth: 220,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {task.last_status}
                              {task.run_count ? ` (${task.run_count}x)` : ""}
                            </Typography>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <ComoSwitch
                        size="small"
                        checked={task.enabled}
                        onChange={(e) =>
                          toggleTask({ id: task.id, enabled: e.target.checked })
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title={t("runNow")}>
                          <IconButton
                            size="small"
                            onClick={() => runNow(task.id)}
                            sx={{ color: color.green700 }}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("edit")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/scheduled_tasks/edit/${task.id}`)
                            }
                            sx={{ color: color.secondary }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("delete")}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedId(task.id);
                              setConfirmationDialogOpen(true);
                            }}
                            sx={{
                              color: color.gray400,
                              "&:hover": { color: color.red },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {visibleTasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ color: color.gray400 }}>
                        {t("emptyState")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleConfirmDelete}
        title={t("deleteTitle")}
        description={t("deleteDescription")}
        content={t("deleteContent")}
        actions={[
          { label: t("no"), onClick: () => handleConfirmDelete(false) },
          { label: t("yes"), onClick: () => handleConfirmDelete(true) },
        ]}
      />
    </Box>
  );
};

export default ScheduledTasksList;
